import type { AccessScope, Permission } from '@whn/shared';
import * as React from 'react';
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  Contact,
  Copy,
  Database,
  FileBarChart,
  FileCode2,
  FileMinus2,
  FileText,
  HandCoins,
  Handshake,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  ListPlus,
  Mail,
  Megaphone,
  Phone,
  Receipt,
  Repeat,
  Route,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  SquareCheck,
  Truck,
  Upload,
  UserRound,
  Users,
  Users2,
  Wallet,
  Zap,
  icons,
  BookMarked,
  ClipboardList,
  ReceiptText,
  Settings2,
  ShoppingCart,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { useCustomModules } from '@/features/custom-modules/use-custom-modules';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: Permission;
  /**
   * Minimum reach the permission must carry. The user directory needs TEAM:
   * `user:read` at OWN scope only lets someone see themselves, which is not a
   * reason to show them an administration screen.
   */
  minScope?: AccessScope;
  /** Modules that arrive in a later phase are shown, disabled, for orientation. */
  comingSoon?: boolean;
}

interface NavSection {
  heading: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    heading: 'Workspace',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/leads', label: 'Leads', icon: Sparkles, permission: 'lead:read' },
      { to: '/contacts', label: 'Contacts', icon: Contact, permission: 'contact:read' },
      { to: '/accounts', label: 'Accounts', icon: Building2, permission: 'account:read' },
      { to: '/deals', label: 'Deals', icon: Handshake, permission: 'deal:read' },
      { to: '/tasks', label: 'Tasks', icon: SquareCheck, permission: 'activity:read' },
      { to: '/meetings', label: 'Meetings', icon: Users2, permission: 'activity:read' },
      { to: '/calls', label: 'Calls', icon: Phone, permission: 'activity:read' },
      { to: '/approvals', label: 'Approvals', icon: ShieldCheck, permission: 'deal:read' },
      { to: '/reports', label: 'Dashboard reports', icon: BarChart3, permission: 'report:read' },
      {
        to: '/reports/custom',
        label: 'Custom reports',
        icon: FileBarChart,
        permission: 'report:read',
      },
    ],
  },
  {
    heading: 'Sales',
    items: [
      { to: '/quotes', label: 'Quotes', icon: FileText, permission: 'quote:read' },
      {
        to: '/sales-orders',
        label: 'Sales orders',
        icon: ScrollText,
        permission: 'salesOrder:read',
      },
      { to: '/invoices', label: 'Invoices', icon: Receipt, permission: 'invoice:read' },
      {
        to: '/credit-notes',
        label: 'Credit Notes',
        icon: FileMinus2,
        permission: 'creditNote:read',
      },
      {
        to: '/payments-received',
        label: 'Payments received',
        icon: HandCoins,
        permission: 'payment:read',
      },
      { to: '/expenses', label: 'Expenses', icon: Wallet, permission: 'expense:read' },
      {
        to: '/recurring-expenses',
        label: 'Recurring Expenses',
        icon: Repeat,
        permission: 'recurringExpense:read',
      },
      { to: '/item-lists', label: 'Item Lists', icon: Boxes, permission: 'itemList:read' },
    ],
  },
  {
    heading: 'Books',
    items: [
      { to: '/books', label: 'Books home', icon: BookMarked },
      { to: '/books/reports', label: 'Financial reports', icon: FileBarChart, permission: 'financeReport:read' },
      { to: '/vendors', label: 'Vendors', icon: Truck, permission: 'vendor:read' },
      {
        to: '/purchase-orders',
        label: 'Purchase orders',
        icon: ShoppingCart,
        permission: 'purchaseOrder:read',
      },
      {
        to: '/expense-reports',
        label: 'Expense reports',
        icon: ClipboardList,
        permission: 'expenseReport:read',
      },
      {
        to: '/gst-invoices',
        label: 'GST invoices',
        icon: ReceiptText,
        permission: 'gstInvoice:read',
      },
      {
        to: '/settings/books',
        label: 'Books settings',
        icon: Settings2,
        permission: 'booksSettings:read',
      },
    ],
  },
  {
    heading: 'Banking',
    items: [
      {
        to: '/banking/accounts',
        label: 'Bank Accounts',
        icon: Landmark,
        permission: 'bankAccount:read',
      },
      {
        to: '/banking/reports',
        label: 'Banking Reports',
        icon: FileBarChart,
        permission: 'bankTransaction:read',
      },
    ],
  },
  {
    heading: 'Support & marketing',
    items: [
      { to: '/cases', label: 'Cases', icon: LifeBuoy, permission: 'case:read' },
      { to: '/solutions', label: 'Solutions', icon: BookOpen, permission: 'solution:read' },
      { to: '/campaigns', label: 'Campaigns', icon: Megaphone, permission: 'campaign:read' },
    ],
  },
  {
    heading: 'Data',
    items: [
      { to: '/import', label: 'Import CSV', icon: Upload, permission: 'lead:import' },
      {
        to: '/settings/zoho-import',
        label: 'Migrate from Zoho',
        icon: ArrowLeftRight,
        permission: 'organization:manage',
      },
      {
        to: '/settings/backups',
        label: 'Backups',
        icon: Database,
        permission: 'organization:manage',
      },
      {
        to: '/settings/duplicates',
        label: 'Data Quality',
        icon: Copy,
        permission: 'organization:manage',
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      {
        to: '/settings/users',
        label: 'Users',
        icon: Users,
        permission: 'user:read',
        minScope: 'TEAM',
      },
      {
        to: '/settings/document-templates',
        label: 'PDF templates',
        icon: FileCode2,
        permission: 'organization:read',
      },
      {
        to: '/settings/organization',
        label: 'Organization',
        icon: Settings,
        permission: 'organization:read',
      },
      { to: '/settings/workflows', label: 'Workflows', icon: Zap, permission: 'workflow:read' },
      {
        to: '/settings/assignment-rules',
        label: 'Assignment rules',
        icon: Route,
        permission: 'workflow:read',
      },
      {
        to: '/settings/custom-fields',
        label: 'Custom fields',
        icon: ListPlus,
        permission: 'customField:read',
      },
      {
        to: '/settings/modules',
        label: 'Modules',
        icon: Boxes,
        permission: 'customField:read',
      },
      {
        to: '/settings/email-templates',
        label: 'Email templates',
        icon: Mail,
        permission: 'email:read',
      },
      {
        to: '/settings/documents',
        label: 'Documents',
        icon: FileText,
        permission: 'quote:read',
      },
      {
        to: '/settings/audit-log',
        label: 'Audit log',
        icon: ScrollText,
        permission: 'auditLog:read',
      },
      { to: '/settings/profile', label: 'My profile', icon: UserRound },
    ],
  },
];

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { canAtScope, user } = useAuth();
  const custom = useCustomModules();

  /*
   * A tenant's own modules take their place beside the built-in ones. Built
   * from the same `NavItem` shape rather than rendered separately, so they
   * get the same active styling, the same permission check, and the same
   * keyboard behaviour without any of it being written twice.
   */
  const sections: NavSection[] = React.useMemo(() => {
    const modules = (custom.data ?? []).filter((module) => module.isActive);
    if (modules.length === 0) return SECTIONS;

    return [
      ...SECTIONS,
      {
        heading: 'Your modules',
        items: modules.map((module) => ({
          to: `/m/${module.apiName}`,
          label: module.pluralLabel,
          // The stored icon is a name, and an unknown one must not crash the
          // whole navigation — so it falls back rather than being trusted.
          icon: (icons[module.icon as keyof typeof icons] as LucideIcon | undefined) ?? Boxes,
          permission: 'customRecord:read' as Permission,
        })),
      },
    ];
  }, [custom.data]);

  return (
    <nav aria-label="Main" className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-4">
      {sections.map((section) => {
        // Hide a whole section when the role can reach nothing inside it.
        const visible = section.items.filter(
          (item) => !item.permission || canAtScope(item.permission, item.minScope ?? 'OWN'),
        );
        if (visible.length === 0) return null;

        return (
          <div key={section.heading}>
            <h2 className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              {section.heading}
            </h2>
            <ul className="space-y-0.5">
              {visible.map((item) => (
                <li key={item.to}>
                  {item.comingSoon ? (
                    <span
                      className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-ink-subtle opacity-60"
                      title="Arriving in the next phase"
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden="true" />
                      <span className="flex-1">{item.label}</span>
                      <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-medium uppercase">
                        Soon
                      </span>
                    </span>
                  ) : (
                    <NavLink
                      to={item.to}
                      end={item.to === '/' || item.to === '/books'}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-brand-soft text-brand-ink'
                            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="size-4 shrink-0" aria-hidden="true" />
                          <span>{item.label}</span>
                          {isActive ? (
                            <span className="sr-only-focusable">(current page)</span>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="mt-auto rounded-md bg-surface-sunken px-3 py-2.5">
        <p className="text-xs font-medium text-ink">{user?.organization.name}</p>
        <p className="text-xs text-ink-subtle">
          {user?.organization.currency} · {user?.organization.timezone}
        </p>
      </div>
    </nav>
  );
}
